import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { validateWebsite } from '@/components/contacts/schema';
import { Icon, Icons } from '@/components/icon';
import { useSidePanelDispatch } from '@/components/sidepanel/side-panel-provider';
import { ActivityCell } from '@/components/table/cells/activity';
import { BooleanCell } from '@/components/table/cells/boolean';
import { CompanyCell, type CompanyCellHandle } from '@/components/table/cells/company';
import {
  DateCell,
  EditableDateCell,
  type EditableDateCellHandle,
} from '@/components/table/cells/date';
import { EmailCell, type EmailCellHandle } from '@/components/table/cells/email';
import { PhoneCell, type PhoneCellHandle } from '@/components/table/cells/phone';
import { TagCell, type TagCellHandle } from '@/components/table/cells/tag';
import type { FilterEvent, FilterableColumn } from '@/components/table/filter-bar';
import type { FilterCondition, FilterValue } from '@/components/table/filter-chip';
import { useAvailableOptionsColumnConfigs } from '@/components/table/filters/use-available-options-config';
import { ShowColumnsButton } from '@/components/table/show-columns-button';
import { SortButton, type SortColumn, type SortOrder } from '@/components/table/sort-button';
import { type ColumnDef, VirtualTable } from '@/components/table/table';
import type { ServerFilter } from '@/components/table/types';
import { mapConditionToOperator, mapOperatorToCondition } from '@/components/table/utils';
import {
  type ColumnIconConfig,
  type TranslationKeyConfig,
  buildFilterableColumns,
  buildSortableColumns,
  getIconForColumnType,
} from '@/components/tables/lib';
import {
  useContactAttributeConfig,
  useUpdateContactAttributeConfig,
} from '@/components/tables/use-contact-attribute-config';
import { useTableDefinition } from '@/components/tables/use-table-definition';
import { ContactAvatar } from '@/components/ui/contact-avatar';
import { Button, PageContent, PageHeader, PageLayout } from '@/components/ui/design-system';
import { Checkbox } from '@/components/ui/design-system/checkbox/checkbox';
import { P3 } from '@/components/ui/text';

import { useServerFilter } from '@/hooks/use-server-filter';
import { useTranslation } from '@/hooks/use-translation';
import { useUpdateContact } from '@/hooks/use-update-contact';
import { getNameFromContact } from '@/lib/conversation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { CreateCompanyModal } from '../companies/components/create-company-modal';
import { ContactViewTabs } from './components/contact-view-tabs';
import { CreateContactModal } from './components/create-contact-modal';
import { PushToPowerDialerBar } from './components/push-to-power-dialer-bar';
import { RenameViewDialog } from './components/rename-view-dialog';
import { SaveViewDialog } from './components/save-view-dialog';
import {
  type ContactView,
  useContactViewsQuery,
  useCreateContactViewMutation,
  useDeleteContactViewMutation,
  useDuplicateContactViewMutation,
  useUpdateContactViewMutation,
} from './queries/use-contact-views';
import { useContactsQuery } from './queries/use-contacts-query';

// ============================================================================
// Contact Column Configuration
// ============================================================================

const CONTACT_COLUMN_ICONS: ColumnIconConfig = {
  name: 'User',
  last_name: 'User',
  phone_numbers: 'Phone',
  phone_number: 'Phone',
  company: 'BuildingOffice',
  emails: 'At',
  website: 'Globe',
  job_title: 'Paperclip',
  last_activity: 'Pulse',
  last_activity_date: 'Pulse',
  activity_history: 'Pulse',
  addresses: 'MapPin',
  sms_content: 'ChatBubble',
  updated_at: 'Dash',
  created_at: 'Dash',
};

const CONTACT_TRANSLATION_KEYS: TranslationKeyConfig = {
  name: 'components.contacts.filter.columns.fullName',
  last_name: 'components.contacts.table.columns.lastName',
  phone_numbers: 'components.contacts.table.columns.phone',
  phone_number: 'components.contacts.table.columns.phone',
  company: 'components.contacts.table.columns.company',
  emails: 'components.contacts.table.columns.emails',
  website: 'components.contacts.table.columns.website',
  job_title: 'components.contacts.table.columns.jobTitle',
  last_activity: 'components.contacts.table.columns.lastActivity',
  last_activity_date: 'components.contacts.table.columns.lastActivityDate',
  activity_history: 'components.contacts.table.columns.activityHistory',
  addresses: 'components.contacts.table.columns.address',
  sms_content: 'components.contacts.table.columns.smsContent',
  updated_at: 'components.contacts.table.columns.updatedAt',
  created_at: 'components.contacts.table.columns.createdAt',
};

function getContactFallbackFilterable(t: (key: string) => string): FilterableColumn[] {
  return [
    { value: 'name', label: t('components.contacts.filter.columns.fullName'), icon: 'User' },
    { value: 'phone_number', label: t('components.contacts.table.columns.phone'), icon: 'Phone' },
    {
      value: 'company',
      label: t('components.contacts.table.columns.company'),
      icon: 'BuildingOffice',
    },
    { value: 'emails', label: t('components.contacts.table.columns.emails'), icon: 'At' },
    { value: 'website', label: t('components.contacts.table.columns.website'), icon: 'Globe' },
    {
      value: 'job_title',
      label: t('components.contacts.table.columns.jobTitle'),
      icon: 'Paperclip',
    },
  ];
}

function getContactFallbackSortable(t: (key: string) => string): SortColumn[] {
  return [
    { value: 'name', label: t('components.contacts.filter.columns.fullName'), icon: 'User' },
    { value: 'phone_number', label: t('components.contacts.table.columns.phone'), icon: 'Phone' },
    {
      value: 'company',
      label: t('components.contacts.table.columns.company'),
      icon: 'BuildingOffice',
    },
    {
      value: 'last_activity_date',
      label: t('components.contacts.table.columns.lastActivity'),
      icon: 'Pulse',
    },
    { value: 'website', label: t('components.contacts.table.columns.website'), icon: 'Globe' },
    {
      value: 'job_title',
      label: t('components.contacts.table.columns.jobTitle'),
      icon: 'Paperclip',
    },
    { value: 'updated_at', label: t('components.contacts.table.columns.updatedAt'), icon: 'Dash' },
    { value: 'created_at', label: t('components.contacts.table.columns.createdAt'), icon: 'Dash' },
  ];
}

// ============================================================================
// Contact Types
// ============================================================================

type Contact = components['schemas']['Contact'];

// Keeps selection state out of the columns useMemo so columns stay stable
// and the table's memoized cells skip re-render on selection changes.
type SelectionContextValue = {
  isSelected: (id: string) => boolean;
  toggle: (id: string, next: boolean) => void;
  toggleAll: (next: boolean) => void;
  total: number;
  selected: number;
};

const SelectionContext = createContext<SelectionContextValue>({
  isSelected: () => false,
  toggle: () => {},
  toggleAll: () => {},
  total: 0,
  selected: 0,
});

const SelectionCellCheckbox = ({
  id,
  ariaLabel,
}: {
  id: string | undefined;
  ariaLabel: string;
}) => {
  const { isSelected, toggle } = useContext(SelectionContext);
  const checked = id ? isSelected(id) : false;
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={(c) => {
        if (id) {
          toggle(id, c === true);
        }
      }}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
    />
  );
};

const SelectionHeaderCheckbox = ({ ariaLabel }: { ariaLabel: string }) => {
  const { total, selected, toggleAll } = useContext(SelectionContext);
  const allSelected = total > 0 && selected === total;
  const someSelected = selected > 0 && selected < total;
  return (
    <Checkbox
      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
      onCheckedChange={(c) => toggleAll(c === true)}
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
    />
  );
};

interface UrlFilter {
  column: string;
  condition: FilterCondition;
  value?: string;
}

export interface ContactsSearchParams {
  filters?: string;
  sortField?: string;
  sortOrder?: SortOrder;
  viewId?: string;
  preselectDemo?: boolean;
}

interface ContactsViewProps {
  searchParams: ContactsSearchParams;
  onNavigate: (updater: (prev: ContactsSearchParams) => ContactsSearchParams) => void;
}

function capitalizeName(name: string | null | undefined) {
  if (!name) {
    return '';
  }
  return name
    .split(' ')
    .map((word) => {
      if (!word) {
        return word;
      }
      const firstChar = word.charAt(0);
      if (/[a-zA-Z]/.test(firstChar)) {
        return firstChar.toUpperCase() + word.slice(1).toLowerCase();
      }
      return word;
    })
    .join(' ');
}

export function parseUrlFilters(filtersParam: unknown): UrlFilter[] {
  if (typeof filtersParam !== 'string') {
    return [];
  }
  try {
    const parsed = JSON.parse(filtersParam);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (f): f is UrlFilter =>
        typeof f === 'object' &&
        f !== null &&
        typeof f.column === 'string' &&
        typeof f.condition === 'string',
    );
  } catch {
    return [];
  }
}

export function ContactsView({ searchParams, onNavigate }: ContactsViewProps) {
  const { t } = useTranslation('desktop_ui');
  const setSidePanelState = useSidePanelDispatch();
  const queryClient = useQueryClient();
  const { updateContact } = useUpdateContact();
  // Fetch table attribute/definition for dynamic columns
  const { data: attributeData } = useTableDefinition('contact');
  const apiColumns = attributeData?.data?.columns ?? [];

  // Fetch user's column config (which columns to show and in what order)
  const { data: attributeConfigData } = useContactAttributeConfig();
  const updateAttributeConfig = useUpdateContactAttributeConfig();
  const attributeConfig = attributeConfigData?.data?.columns;

  // Fetch business tags for allows_array columns (tag selector)
  const { data: tagsData } = $apiAllo.useQuery('get', '/v1/business/tags', {});

  // Build column configs for filters with available_options (e.g., last_activity)
  const columnConfigs = useAvailableOptionsColumnConfigs(apiColumns);

  const [selection, setSelection] = useState<Record<string, boolean>>({});

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
  const [createCompanyForContact, setCreateCompanyForContact] = useState<{
    id: string;
    name: string;
    last_name: string;
  } | null>(null);

  // View state
  const [isSaveViewDialogOpen, setIsSaveViewDialogOpen] = useState(false);
  const [isRenameViewDialogOpen, setIsRenameViewDialogOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [viewToRename, setViewToRename] = useState<ContactView | null>(null);
  const [viewToDelete, setViewToDelete] = useState<ContactView | null>(null);
  const [viewToDuplicate, setViewToDuplicate] = useState<ContactView | null>(null);

  // Fetch views
  const { data: viewsData } = useContactViewsQuery();
  const views = ((viewsData as any)?.data?.views ?? []) as ContactView[];

  // View mutations
  const createViewMutation = useCreateContactViewMutation({
    onSuccess: (view) => {
      setIsSaveViewDialogOpen(false);
      // Navigate to the new view with its filters and sort
      const viewFilters = view.filter.and.map((f) => ({
        column: f.field,
        condition: mapOperatorToCondition(f.operator),
        value: f.value || undefined,
      }));
      onNavigate(() => ({
        viewId: view.id,
        filters: viewFilters.length > 0 ? JSON.stringify(viewFilters) : undefined,
        sortField: view.sort?.[0]?.field,
        sortOrder: view.sort?.[0]?.order as SortOrder | undefined,
      }));
    },
  });

  const updateViewMutation = useUpdateContactViewMutation({
    onSuccess: () => {
      setIsRenameViewDialogOpen(false);
      setViewToRename(null);
    },
  });

  const deleteViewMutation = useDeleteContactViewMutation({
    onSuccess: () => {
      setIsDeleteConfirmationOpen(false);
      setViewToDelete(null);
      // If we deleted the active view, go back to "All"
      if (searchParams.viewId === viewToDelete?.id) {
        onNavigate(() => ({}));
      }
    },
  });

  const duplicateViewMutation = useDuplicateContactViewMutation({
    onSuccess: (view) => {
      setIsSaveViewDialogOpen(false);
      setViewToDuplicate(null);
      // Navigate to the duplicated view with its filters and sort
      const viewFilters = view.filter.and.map((f) => ({
        column: f.field,
        condition: mapOperatorToCondition(f.operator),
        value: f.value || undefined,
      }));
      onNavigate(() => ({
        viewId: view.id,
        filters: viewFilters.length > 0 ? JSON.stringify(viewFilters) : undefined,
        sortField: view.sort?.[0]?.field,
        sortOrder: view.sort?.[0]?.order as SortOrder | undefined,
      }));
    },
  });

  // Get active view ID from URL params
  const activeViewId = searchParams.viewId ?? null;

  // Get the active view object
  const activeView = useMemo(() => {
    if (!activeViewId) {
      return null;
    }
    return views.find((v) => v.id === activeViewId) ?? null;
  }, [activeViewId, views]);

  // Check if current filters/sort differ from the active view's saved config
  const hasUnsavedChanges = useMemo(() => {
    if (!activeViewId || !activeView) {
      return false;
    }

    // Compare sort - normalize both sides (case-insensitive, with defaults)
    // Default sort is last_activity_date/desc when not specified
    const currentSortField = (searchParams.sortField ?? 'last_activity_date').toLowerCase();
    const currentSortOrder = (searchParams.sortOrder ?? 'desc').toLowerCase();
    const viewSortField = (activeView.sort?.[0]?.field ?? 'last_activity_date').toLowerCase();
    const viewSortOrder = (activeView.sort?.[0]?.order ?? 'desc').toLowerCase();

    // Sort is different if the normalized values don't match
    const sortChanged = currentSortField !== viewSortField || currentSortOrder !== viewSortOrder;

    if (sortChanged) {
      return true;
    }

    // Compare filters (case-insensitive for column and condition)
    const currentFilters = parseUrlFilters(searchParams.filters).map((f) => ({
      column: f.column?.toLowerCase() || '',
      condition: f.condition?.toLowerCase() || '',
      value: f.value || '',
    }));
    const viewFilters = activeView.filter.and.map((f) => ({
      column: f.field?.toLowerCase() || '',
      condition: mapOperatorToCondition(f.operator)?.toLowerCase() || '',
      value: f.value || '',
    }));

    return JSON.stringify(currentFilters) !== JSON.stringify(viewFilters);
  }, [
    activeViewId,
    activeView,
    searchParams.filters,
    searchParams.sortField,
    searchParams.sortOrder,
  ]);

  // Handle view selection
  const handleViewSelect = useCallback(
    (viewId: string | null) => {
      if (!viewId) {
        // "All" view - clear filters and viewId
        onNavigate(() => ({}));
        return;
      }

      const view = views.find((v) => v.id === viewId);
      if (!view) {
        return;
      }

      // Convert view filter format to URL filter format
      const urlFilters: UrlFilter[] = view.filter.and.map((f) => ({
        column: f.field,
        condition: mapOperatorToCondition(f.operator),
        value: f.value || undefined,
      }));

      // Apply view's filters and sort to URL
      onNavigate(() => ({
        viewId,
        filters: urlFilters.length > 0 ? JSON.stringify(urlFilters) : undefined,
        sortField: view.sort?.[0]?.field,
        sortOrder: view.sort?.[0]?.order as SortOrder | undefined,
      }));
    },
    [views, onNavigate],
  );

  // Handle creating a new view
  const handleCreateView = useCallback(() => {
    setIsSaveViewDialogOpen(true);
  }, []);

  // Handle saving a new view
  const handleSaveNewView = useCallback(
    (name: string, access: 'USER' | 'TEAM') => {
      const urlFiltersArr = parseUrlFilters(searchParams.filters);

      createViewMutation.mutate({
        body: {
          name,
          filter: {
            and: urlFiltersArr.map((f) => ({
              field: f.column,
              operator: mapConditionToOperator(f.condition),
              value: f.value ?? '',
            })),
          },
          sort:
            searchParams.sortField || searchParams.sortOrder
              ? [
                  {
                    field: searchParams.sortField ?? 'last_activity_date',
                    order: searchParams.sortOrder ?? 'desc',
                  },
                ]
              : null,
          owner_type: access,
        } as any,
      });
    },
    [searchParams.filters, searchParams.sortField, searchParams.sortOrder, createViewMutation],
  );

  // Handle saving changes to an existing view
  const handleSaveChanges = useCallback(() => {
    if (!activeViewId) {
      return;
    }

    const urlFiltersArr = parseUrlFilters(searchParams.filters);

    // Capture current sort values to use in onSuccess
    const savedSortField = searchParams.sortField || 'last_activity_date';
    const savedSortOrder = searchParams.sortOrder || 'desc';

    updateViewMutation.mutate({
      params: { path: { viewId: activeViewId } },
      body: {
        filter: {
          and: urlFiltersArr.map((f) => ({
            field: f.column,
            operator: mapConditionToOperator(f.condition),
            value: f.value ?? '',
          })),
        },
        sort:
          searchParams.sortField || searchParams.sortOrder
            ? [
                {
                  field: savedSortField,
                  order: savedSortOrder,
                },
              ]
            : null,
      },
    });
  }, [
    activeViewId,
    searchParams.filters,
    searchParams.sortField,
    searchParams.sortOrder,
    updateViewMutation,
  ]);

  // Handle renaming a view
  const handleRenameView = useCallback((view: ContactView) => {
    setViewToRename(view);
    setIsRenameViewDialogOpen(true);
  }, []);

  const handleRenameSubmit = useCallback(
    (name: string, access: 'USER' | 'TEAM') => {
      if (!viewToRename) {
        return;
      }

      updateViewMutation.mutate({
        params: { path: { viewId: viewToRename.id } },
        body: { name, owner_type: access } as any,
      });
    },
    [viewToRename, updateViewMutation],
  );

  // Handle duplicating a view - opens dialog first
  const handleDuplicateView = useCallback((view: ContactView) => {
    setViewToDuplicate(view);
    setIsSaveViewDialogOpen(true);
  }, []);

  // Handle duplicate from dialog
  const handleDuplicateFromDialog = useCallback(
    (viewId: string, name: string, access: 'USER' | 'TEAM') => {
      duplicateViewMutation.mutate({
        params: { path: { viewId } },
        body: { name, owner_type: access } as any,
      });
    },
    [duplicateViewMutation],
  );

  // Handle deleting a view
  const handleDeleteView = useCallback((view: ContactView) => {
    setViewToDelete(view);
    setIsDeleteConfirmationOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!viewToDelete) {
      return;
    }

    deleteViewMutation.mutate({
      params: { path: { viewId: viewToDelete.id } },
    });
  }, [viewToDelete, deleteViewMutation]);

  // Handle deleted view in URL - fallback to "All"
  useEffect(() => {
    if (
      activeViewId &&
      views.length > 0 &&
      !views.find((v: { id?: string }) => v.id === activeViewId)
    ) {
      // View was deleted or doesn't exist, fallback to "All"
      onNavigate(() => ({}));
    }
  }, [activeViewId, views, onNavigate]);

  const phoneCellRefs = useRef<Map<string, PhoneCellHandle>>(new Map());
  const emailCellRefs = useRef<Map<string, EmailCellHandle>>(new Map());
  const companyCellRefs = useRef<Map<string, CompanyCellHandle>>(new Map());
  const dateCellRefs = useRef<Map<string, EditableDateCellHandle>>(new Map());
  const tagCellRefs = useRef<Map<string, TagCellHandle>>(new Map());
  const [openPhonePopoverContactId, setOpenPhonePopoverContactId] = useState<string | null>(null);
  const [openEmailPopoverContactId, setOpenEmailPopoverContactId] = useState<string | null>(null);
  const [openCompanyPopoverContactId, setOpenCompanyPopoverContactId] = useState<string | null>(
    null,
  );

  const openPhonePopoverContactIdRef = useRef<string | null>(null);
  openPhonePopoverContactIdRef.current = openPhonePopoverContactId;
  const openEmailPopoverContactIdRef = useRef<string | null>(null);
  openEmailPopoverContactIdRef.current = openEmailPopoverContactId;
  const openCompanyPopoverContactIdRef = useRef<string | null>(null);
  openCompanyPopoverContactIdRef.current = openCompanyPopoverContactId;

  const handleOpenSidepanelRef = useRef<(id?: string) => void>(() => {});

  // Build filterable columns from API or use fallback
  const filterableColumns = useMemo(() => {
    const translate = (key: string) => t(key as never);
    if (apiColumns.length > 0) {
      return buildFilterableColumns(
        apiColumns.filter((col) => col.key !== 'addresses'),
        translate,
        CONTACT_COLUMN_ICONS,
        CONTACT_TRANSLATION_KEYS,
      );
    }
    return getContactFallbackFilterable(translate);
  }, [apiColumns, t]);

  // Build sortable columns from API or use fallback
  const sortableColumns: SortColumn[] = useMemo(() => {
    const translate = (key: string) => t(key as never);
    if (apiColumns.length > 0) {
      const columns = buildSortableColumns(
        apiColumns,
        translate,
        CONTACT_COLUMN_ICONS,
        CONTACT_TRANSLATION_KEYS,
      );
      // Ensure last_activity_date is always in sortable columns (default sort field)
      if (!columns.some((col) => col.value === 'last_activity_date')) {
        columns.push({
          value: 'last_activity_date',
          label: translate('components.contacts.table.columns.lastActivity'),
          icon: 'Pulse',
        });
      }
      return columns;
    }
    return getContactFallbackSortable(translate);
  }, [apiColumns, t]);

  const sortField = searchParams.sortField ?? 'last_activity_date';

  const sortOrder = searchParams.sortOrder ?? 'desc';

  const isCustomSort = sortField !== 'last_activity_date' || sortOrder !== 'desc';

  const urlFilters = useMemo(() => parseUrlFilters(searchParams.filters), [searchParams.filters]);

  const initialFilters = useMemo((): FilterValue[] => {
    // Build reverse map: serverField -> columnKey for columns with custom conditions
    const serverFieldToColumn = new Map<string, string>();
    for (const [columnKey, config] of Object.entries(columnConfigs)) {
      if (config.customConditions) {
        for (const cond of config.customConditions) {
          serverFieldToColumn.set(cond.serverField, columnKey);
        }
      }
    }

    const result: FilterValue[] = [];
    for (const f of urlFilters) {
      let column = filterableColumns.find((c) => c.value === f.column);
      let columnId = f.column;
      let serverField: string | undefined;

      // If not found directly, the URL may store a serverField (e.g. event_user_account)
      // instead of the column key (e.g. last_activity). Resolve it via columnConfigs.
      if (!column) {
        const mappedKey = serverFieldToColumn.get(f.column);
        if (mappedKey) {
          column = filterableColumns.find((c) => c.value === mappedKey);
          columnId = mappedKey;
          serverField = f.column;
        }
      }

      // Resolve serverField from custom conditions config when condition is custom (from, to, etc.)
      if (column && !serverField) {
        const config = columnConfigs[columnId];
        if (config?.customConditions) {
          const customCond = config.customConditions.find((c) => c.value === f.condition);
          if (customCond) {
            serverField = customCond.serverField;
          }
        }
      }

      if (column) {
        result.push({
          id: `url-filter-${f.column}-${f.condition}-${f.value ?? ''}`,
          columnId,
          columnLabel: column.label,
          columnIcon: column.icon,
          condition: f.condition,
          value: f.value,
          serverField,
        });
      }
    }
    return result;
  }, [urlFilters, filterableColumns, columnConfigs]);

  const initialServerFilter = useMemo((): ServerFilter | null => {
    if (urlFilters.length === 0) {
      return isCustomSort ? { and: [] } : null;
    }

    const conditionsWithoutValue = ['empty', 'not_empty'];

    const validFilters = urlFilters.filter((f) => {
      if (!conditionsWithoutValue.includes(f.condition)) {
        return f.value && f.value.trim() !== '';
      }
      return true;
    });

    if (validFilters.length === 0) {
      return isCustomSort ? { and: [] } : null;
    }

    const conditions = validFilters.map((f) => ({
      field: f.column,
      operator: mapConditionToOperator(f.condition),
      value: f.value ?? '',
    }));

    return { and: conditions };
  }, [urlFilters, isCustomSort]);

  const handlePhoneAdd = useCallback(
    (contactId: string, currentNumbers: string[], phone: string) => {
      if (currentNumbers.includes(phone)) {
        return;
      }
      updateContact(contactId, { numbers: [...currentNumbers, phone] });
    },
    [updateContact],
  );

  const handlePhoneDelete = useCallback(
    (contactId: string, currentNumbers: string[], phone: string) => {
      updateContact(contactId, { numbers: currentNumbers.filter((n) => n !== phone) });
    },
    [updateContact],
  );

  const handleEmailAdd = useCallback(
    (contactId: string, currentEmails: string[], email: string) => {
      if (currentEmails.includes(email)) {
        return;
      }
      updateContact(contactId, { emails: [...currentEmails, email] });
    },
    [updateContact],
  );

  const handleEmailDelete = useCallback(
    (contactId: string, currentEmails: string[], email: string) => {
      updateContact(contactId, { emails: currentEmails.filter((e) => e !== email) });
    },
    [updateContact],
  );

  const handleCompanySelect = useCallback(
    (contactId: string, selectedCompany: components['schemas']['Company'] | null) => {
      updateContact(
        contactId,
        { company: selectedCompany?.id ?? null },
        { company: selectedCompany },
      );
    },
    [updateContact],
  );

  const handleCompanyClick = useCallback(
    (companyId: string, companyName: string) => {
      setSidePanelState({
        open: true,
        mode: 'companies',
        context: { companyId },
        entityInfo: { name: companyName },
      });
    },
    [setSidePanelState],
  );

  const handleCreateCompanyForContact = useCallback(
    (contact: { id: string; name: string; last_name: string }) => {
      setCreateCompanyForContact(contact);
      setIsCreateCompanyModalOpen(true);
    },
    [],
  );

  const handleCompanyCreated = useCallback(
    (company: components['schemas']['CompanyResponse']) => {
      // Associate the newly created company with the contact
      if (createCompanyForContact?.id && company.id) {
        updateContact(createCompanyForContact.id, { company: company.id }, { company });
      }
      setCreateCompanyForContact(null);
    },
    [createCompanyForContact, updateContact],
  );

  // Helper to get column label from API, with translation fallback
  const getColumnLabel = useCallback(
    (key: string, translationKey: string) => {
      const apiColumn = apiColumns.find((col) => col.key === key);
      return apiColumn?.label ?? t(translationKey as never);
    },
    [apiColumns, t],
  );

  // Mapping from column ID to API key (for columns where they differ)
  const columnIdToApiKey: Record<string, string> = useMemo(
    () => ({
      phone: 'phone_numbers',
      activity: 'last_activity',
      jobTitle: 'job_title',
    }),
    [],
  );

  // Default visible columns when no contact_attribute_config exists
  const DEFAULT_VISIBLE_COLUMNS = useMemo(
    () =>
      new Set([
        'name',
        'phone_numbers',
        'company',
        'job_title',
        'emails',
        'last_activity',
        'website',
        'addresses',
        'created_at',
        'updated_at',
      ]),
    [],
  );

  // Helper to check if column should be visible based on attribute config
  const isColumnVisible = useCallback(
    (columnId: string) => {
      const apiKey = columnIdToApiKey[columnId] ?? columnId;
      // If no config exists yet, use hardcoded defaults (ignores old attribute is_visible)
      if (!attributeConfig || attributeConfig.length === 0) {
        return DEFAULT_VISIBLE_COLUMNS.has(apiKey);
      }
      const configItem = attributeConfig.find((c) => c.column_key === apiKey);
      return configItem?.is_visible ?? false;
    },
    [attributeConfig, DEFAULT_VISIBLE_COLUMNS],
  );

  const pinnedColumns = ['name', 'phone_numbers'];

  const handleColumnReorder = useCallback(
    (columnIds: string[]) => {
      const visibleColumnIds = columnIds.filter(
        (id) => !pinnedColumns.includes(columnIdToApiKey[id] ?? id),
      );
      const pinnedEntries = pinnedColumns.map((key, index) => ({
        column_key: key,
        display_order: index,
        is_visible: true,
      }));
      const offset = pinnedEntries.length;
      // Visible columns being reordered
      const visibleEntries = visibleColumnIds.map((id, index) => ({
        column_key: columnIdToApiKey[id] ?? id,
        display_order: offset + index,
        is_visible: true,
      }));
      // Hidden columns preserve their order after visible ones
      const visibleKeys = new Set([
        ...pinnedColumns,
        ...visibleColumnIds.map((id) => columnIdToApiKey[id] ?? id),
      ]);
      const hiddenEntries = (attributeConfig ?? [])
        .filter((c) => !c.is_visible && !visibleKeys.has(c.column_key))
        .sort((a, b) => a.display_order - b.display_order)
        .map((c, index) => ({
          column_key: c.column_key,
          display_order: offset + visibleColumnIds.length + index,
          is_visible: false,
        }));
      updateAttributeConfig.mutate([...pinnedEntries, ...visibleEntries, ...hiddenEntries]);
    },
    [updateAttributeConfig, columnIdToApiKey, attributeConfig],
  );

  const columns: ColumnDef<Contact, unknown>[] = useMemo(() => {
    const baseColumns = (
      [
        {
          id: 'name',
          accessorFn: (row) => {
            const firstName = row.name ?? '';
            const lastName = row.last_name ?? '';
            return `${firstName} ${lastName}`.trim();
          },
          cell: ({ row }) => {
            const contact = row.original;
            const firstName = capitalizeName(row.original.name);
            const lastName = capitalizeName(row.original.last_name);
            const id = row.original.id;
            const fullName = `${firstName} ${lastName}`.trim();

            return (
              <div className="flex h-full w-full items-center gap-3 text-primary-100" key={id}>
                {/* preventDefault on mousedown blocks the focus shift that would
                    trigger the cell's :focus-within open behavior. */}
                <span
                  className="flex shrink-0 items-center"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <SelectionCellCheckbox
                    id={id}
                    ariaLabel={t('components.contacts.table.select')}
                  />
                </span>
                <button
                  type="button"
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                  onClick={() => handleOpenSidepanelRef.current(id)}
                >
                  <ContactAvatar
                    name={getNameFromContact(contact)}
                    size="sm"
                    imageUrl={contact.image_url ?? undefined}
                  />
                  <span className="truncate text-heading-14">
                    {fullName || t('components.contacts.table.unknown')}
                  </span>
                </button>
              </div>
            );
          },
          header: () => {
            const label = getColumnLabel('name', 'components.contacts.table.columns.name');
            return (
              // pl-px compensates for the 1px transparent border body TableCell
              // reserves for its focus state — without it, the header checkbox
              // sits 1px left of the body checkboxes.
              <span className="flex items-center gap-3 pl-px">
                <span
                  className="flex shrink-0 items-center"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <SelectionHeaderCheckbox ariaLabel={t('components.contacts.table.selectAll')} />
                </span>
                <Icon name={Icons.User} className="shrink-0 text-m text-primary-100" />
                <span>{label}</span>
              </span>
            );
          },
          // Don't set meta.icon — the header function renders the icon itself
          // alongside the select-all checkbox so the visual order stays
          // [checkbox] [icon] [label]. Filter chip icon still comes from
          // CONTACT_COLUMN_ICONS via buildFilterableColumns.
          size: 212,
          minSize: 212,
        },
        {
          id: 'phone',
          accessorFn: (row) => row.numbers?.join(', ') ?? '',
          header: getColumnLabel('phone_numbers', 'components.contacts.table.columns.phone'),
          meta: {
            icon: 'Phone',
            skipDefaultCell: true,
            elevateOnFocus: true,
          },
          cell: ({ row }) => {
            const contactId = row.original.id;
            const phoneNumbers = row.original.numbers ?? [];
            const contactName =
              `${capitalizeName(row.original.name)} ${capitalizeName(row.original.last_name)}`.trim();
            return (
              <PhoneCell
                ref={(handle) => {
                  if (contactId) {
                    if (handle) {
                      phoneCellRefs.current.set(contactId, handle);
                    } else {
                      phoneCellRefs.current.delete(contactId);
                    }
                  }
                }}
                contactId={contactId}
                contactName={contactName}
                phoneNumbers={phoneNumbers}
                isOpen={openPhonePopoverContactIdRef.current === contactId}
                onOpenChange={(open) =>
                  setOpenPhonePopoverContactId(open ? (contactId ?? null) : null)
                }
                onPhoneAdd={handlePhoneAdd}
                onPhoneDelete={handlePhoneDelete}
              />
            );
          },
        },
        {
          id: 'company',
          accessorFn: (row) => row.company?.name ?? '',
          header: getColumnLabel('company', 'components.contacts.table.columns.company'),
          meta: {
            icon: 'BuildingOffice',
            skipDefaultCell: true,
          },
          cell: ({ row }) => {
            const contact = row.original;
            const contactId = contact.id;
            const company = contact.company as
              | components['schemas']['CompanyResponse']
              | null
              | undefined;
            return (
              <CompanyCell
                ref={(handle) => {
                  if (contactId) {
                    if (handle) {
                      companyCellRefs.current.set(contactId, handle);
                    } else {
                      companyCellRefs.current.delete(contactId);
                    }
                  }
                }}
                contactId={contactId}
                company={company}
                isOpen={openCompanyPopoverContactIdRef.current === contactId}
                onOpenChange={(open) =>
                  setOpenCompanyPopoverContactId(open ? (contactId ?? null) : null)
                }
                onSelectCompany={handleCompanySelect}
                onCompanyClick={handleCompanyClick}
                onCreateCompany={() =>
                  contactId &&
                  handleCreateCompanyForContact({
                    id: contactId,
                    name: contact.name ?? '',
                    last_name: contact.last_name ?? '',
                  })
                }
              />
            );
          },
        },
        {
          id: 'activity',
          accessorFn: (row) => row.activity?.[0]?.type ?? '',
          header: getColumnLabel('last_activity', 'components.contacts.table.columns.lastActivity'),
          meta: { icon: 'Pulse', disabled: true },
          cell: ({ row }) => (
            <ActivityCell activity={row.original.activity?.[0]} className="flex-nowrap" />
          ),
        },
        {
          id: 'emails',
          accessorFn: (row) => row.emails?.join(', ') ?? '',
          header: getColumnLabel('emails', 'components.contacts.table.columns.emails'),
          meta: {
            icon: 'At',
            skipDefaultCell: true,
            elevateOnFocus: true,
          },
          cell: ({ row }) => {
            const contactId = row.original.id;
            const emails = row.original.emails ?? [];
            return (
              <EmailCell
                ref={(handle) => {
                  if (contactId) {
                    if (handle) {
                      emailCellRefs.current.set(contactId, handle);
                    } else {
                      emailCellRefs.current.delete(contactId);
                    }
                  }
                }}
                contactId={contactId}
                emails={emails}
                isOpen={openEmailPopoverContactIdRef.current === contactId}
                onOpenChange={(open) =>
                  setOpenEmailPopoverContactId(open ? (contactId ?? null) : null)
                }
                onEmailAdd={handleEmailAdd}
                onEmailDelete={handleEmailDelete}
              />
            );
          },
        },
        {
          id: 'website',
          accessorFn: (row) => row.website ?? '',
          cell: ({ row }) => {
            const url = row.original.website ?? '';
            if (!url) {
              return null;
            }
            const hrefUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
            const displayUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
            return (
              <a
                href={hrefUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="truncate font-medium text-primary-100 text-s underline"
                onClick={(e) => e.stopPropagation()}
              >
                {displayUrl}
              </a>
            );
          },
          header: getColumnLabel('website', 'components.contacts.table.columns.website'),
          meta: {
            icon: 'Globe',
            editable: true,
            onEdit: (row, newValue) => {
              const contact = row as Contact;
              if (!contact.id) {
                return false;
              }

              if (!newValue.trim()) {
                updateContact(contact.id, { website: '' });
                return;
              }

              if (!validateWebsite(newValue.trim())) {
                toast.error(t('components.contacts.table.invalidUrl'));
                return false;
              }

              let url = newValue.trim();
              if (!/^https?:\/\//i.test(url)) {
                url = `https://${url}`;
              }

              updateContact(contact.id, { website: url });
              return;
            },
          },
        },
        {
          id: 'jobTitle',
          accessorFn: (row) => row.job_title ?? '',
          header: getColumnLabel('job_title', 'components.contacts.table.columns.jobTitle'),
          meta: {
            icon: 'Paperclip',
            editable: true,
            onEdit: (row, newValue) => {
              const contact = row as Contact;
              if (!contact.id) {
                return false;
              }
              updateContact(contact.id, { job_title: newValue });
              return;
            },
          },
        },
        {
          id: 'addresses',
          accessorFn: (row) => {
            const addresses = row.addresses;
            if (!addresses) {
              return '';
            }
            return Array.isArray(addresses) ? (addresses[0] ?? '') : addresses;
          },
          header: getColumnLabel('addresses', 'components.contacts.table.columns.address'),
          meta: {
            icon: 'MapPin',
            editable: true,
            onEdit: (row, newValue) => {
              const contact = row as Contact;
              if (!contact.id) {
                return false;
              }
              const currentAddresses = contact.addresses;
              const addresses = newValue.trim()
                ? [newValue, ...(Array.isArray(currentAddresses) ? currentAddresses.slice(1) : [])]
                : [];
              updateContact(contact.id, { addresses });
              return;
            },
          },
        },
        {
          id: 'updated_at',
          cell: ({ row }) => <DateCell value={row.original.updated_at} />,
          header: getColumnLabel('updated_at', 'components.contacts.table.columns.updatedAt'),
          meta: { disabled: true },
        },
        {
          id: 'created_at',
          cell: ({ row }) => <DateCell value={row.original.created_at} />,
          header: getColumnLabel('created_at', 'components.contacts.table.columns.createdAt'),
          meta: { disabled: true },
        },
      ] as ColumnDef<Contact, unknown>[]
    ).filter((col) => isColumnVisible(col.id ?? ''));

    // Known column keys that have custom renderers
    const knownColumnKeys = new Set([
      'name',
      'phone_numbers',
      'company',
      'last_activity',
      'emails',
      'website',
      'job_title',
      'addresses',
      'updated_at',
      'created_at',
    ]);

    // Helper to read a dynamic column value from either top-level or custom_properties
    const getDynamicValue = (row: Contact, key: string) => {
      const r = row as Record<string, unknown>;
      return r[key] ?? (r.custom_properties as Record<string, unknown> | undefined)?.[key];
    };

    // Add dynamic columns from API that aren't in the known list
    const dynamicColumns = apiColumns
      .filter(
        (col) => !knownColumnKeys.has(col.key) && isColumnVisible(col.key) && !col.hide_column,
      )
      .map((col) => {
        const isEditable = col.editable !== false;
        const translationKey = CONTACT_TRANSLATION_KEYS[col.key];
        const columnHeader = col.label ?? (translationKey ? t(translationKey as never) : col.key);
        const hasOptions = (col.options?.length ?? 0) > 0;
        const columnIcon =
          (CONTACT_COLUMN_ICONS[col.key] as keyof typeof Icons | undefined) ??
          getIconForColumnType(col.type, { hasOptions });

        // Columns with predefined options use TagCell selector
        if (col.options?.length) {
          const optionColors = [
            '#4576ed', // blue
            '#9e3dff', // purple
            '#eb00eb', // pink
            '#ff4539', // red
            '#ff8d28', // orange
            '#ffc300', // yellow
            '#2fd159', // green
            '#008a60', // highlight
          ];
          const optionTags = col.options.map((opt, i) => ({
            key: opt.key,
            name: opt.label ?? opt.key,
            hexColor: optionColors[i % optionColors.length],
          }));
          const openTagCell = (e: React.KeyboardEvent, row: Contact) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const refKey = `${row.id}:${col.key}`;
              tagCellRefs.current.get(refKey)?.open();
            }
          };
          return {
            id: col.key,
            header: columnHeader,
            meta: {
              icon: columnIcon,
              skipDefaultCell: true,
              onKeyDown: openTagCell,
            },
            cell: ({ row }: { row: { original: Contact } }) => {
              const contact = row.original;
              const rawValue = getDynamicValue(contact, col.key);
              const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
              const refKey = `${contact.id}:${col.key}`;
              return (
                <TagCell
                  ref={(handle) => {
                    if (handle) {
                      tagCellRefs.current.set(refKey, handle);
                    } else {
                      tagCellRefs.current.delete(refKey);
                    }
                  }}
                  tags={optionTags}
                  value={value as string | undefined}
                  contactId={isEditable ? contact.id : undefined}
                  columnKey={col.key}
                  onValueChange={
                    isEditable
                      ? (contactId, columnKey, newValue) => {
                          updateContact(contactId, {
                            custom_properties: { [columnKey]: newValue },
                          });
                        }
                      : undefined
                  }
                />
              );
            },
          } as ColumnDef<Contact, unknown>;
        }

        // TEXT columns with allows_array use TagCell selector (business tags)
        if (col.type === 'TEXT' && col.allows_array) {
          const tags = tagsData?.data ?? [];
          const openTagCell = (e: React.KeyboardEvent, row: Contact) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const refKey = `${row.id}:${col.key}`;
              tagCellRefs.current.get(refKey)?.open();
            }
          };
          return {
            id: col.key,
            header: columnHeader,
            meta: {
              icon: columnIcon,
              skipDefaultCell: true,
              onKeyDown: openTagCell,
            },
            cell: ({ row }: { row: { original: Contact } }) => {
              const contact = row.original;
              const rawValue = getDynamicValue(contact, col.key);
              const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
              const refKey = `${contact.id}:${col.key}`;
              return (
                <TagCell
                  ref={(handle) => {
                    if (handle) {
                      tagCellRefs.current.set(refKey, handle);
                    } else {
                      tagCellRefs.current.delete(refKey);
                    }
                  }}
                  tags={tags}
                  value={value as string | undefined}
                  contactId={isEditable ? contact.id : undefined}
                  columnKey={col.key}
                  onValueChange={
                    isEditable
                      ? (contactId, columnKey, newValue) => {
                          updateContact(contactId, {
                            custom_properties: { [columnKey]: newValue },
                          });
                        }
                      : undefined
                  }
                />
              );
            },
          } as ColumnDef<Contact, unknown>;
        }

        // BOOLEAN columns render a checkbox
        if (col.type === 'BOOLEAN') {
          return {
            id: col.key,
            accessorKey: col.key,
            header: columnHeader,
            meta: {
              icon: columnIcon,
            },
            cell: ({ row }: { row: { original: Contact } }) => {
              const contact = row.original;
              const value = getDynamicValue(contact, col.key) as boolean | undefined;
              return (
                <BooleanCell
                  value={value}
                  onChange={
                    isEditable
                      ? (checked) => {
                          if (contact.id) {
                            updateContact(contact.id, {
                              custom_properties: { [col.key]: checked },
                            });
                          }
                        }
                      : undefined
                  }
                />
              );
            },
          } as ColumnDef<Contact, unknown>;
        }

        // DATE columns render a calendar popover for editing
        if (col.type === 'DATE') {
          return {
            id: col.key,
            accessorKey: col.key,
            header: columnHeader,
            meta: {
              icon: columnIcon,
              skipDefaultCell: isEditable,
            },
            cell: ({ row }: { row: { original: Contact } }) => {
              const contact = row.original;
              const value = getDynamicValue(contact, col.key) as string | undefined;
              if (!isEditable) {
                return <DateCell value={value} />;
              }
              const refKey = `${contact.id}:${col.key}`;
              return (
                <EditableDateCell
                  ref={(handle) => {
                    if (handle) {
                      dateCellRefs.current.set(refKey, handle);
                    } else {
                      dateCellRefs.current.delete(refKey);
                    }
                  }}
                  value={value}
                  onChange={(newValue) => {
                    if (contact.id) {
                      updateContact(contact.id, {
                        custom_properties: { [col.key]: newValue },
                      });
                    }
                  }}
                />
              );
            },
          } as ColumnDef<Contact, unknown>;
        }

        // NUMBER columns use number input
        if (col.type === 'NUMBER') {
          return {
            id: col.key,
            header: columnHeader,
            accessorFn: (row: Contact) => getDynamicValue(row, col.key) ?? '',
            meta: {
              icon: columnIcon,
              editable: isEditable,
              editInputType: 'number' as const,
              onEdit: isEditable
                ? (row: Contact, newValue: string) => {
                    const contact = row;
                    if (!contact.id) {
                      return false;
                    }
                    const numValue = newValue.trim() === '' ? null : Number(newValue);
                    if (numValue !== null && Number.isNaN(numValue)) {
                      return false;
                    }
                    updateContact(contact.id, {
                      custom_properties: { [col.key]: numValue },
                    });
                    return undefined;
                  }
                : undefined,
            },
          } as ColumnDef<Contact, unknown>;
        }

        // TEXT columns (non-array) use inline text editing
        return {
          id: col.key,
          header: columnHeader,
          accessorFn: (row: Contact) => getDynamicValue(row, col.key) ?? '',
          meta: {
            icon: columnIcon,
            editable: isEditable,
            onEdit: isEditable
              ? (row: Contact, newValue: string) => {
                  const contact = row;
                  if (!contact.id) {
                    return false;
                  }
                  updateContact(contact.id, {
                    custom_properties: { [col.key]: newValue },
                  });
                  return undefined;
                }
              : undefined,
          },
        } as ColumnDef<Contact, unknown>;
      });

    const allColumns = [...baseColumns, ...dynamicColumns];

    // Reorder columns based on attribute config if available
    if (attributeConfig && attributeConfig.length > 0) {
      const orderMap = new Map(attributeConfig.map((c) => [c.column_key, c.display_order]));
      // Reverse map: column IDs that differ from API keys
      const apiKeyToColumnId: Record<string, string> = {};
      for (const [colId, apiKey] of Object.entries(columnIdToApiKey)) {
        apiKeyToColumnId[apiKey] = colId;
      }

      allColumns.sort((a, b) => {
        const aId = a.id ?? '';
        const bId = b.id ?? '';
        const aApiKey = columnIdToApiKey[aId] ?? aId;
        const bApiKey = columnIdToApiKey[bId] ?? bId;
        const aOrder = orderMap.get(aApiKey) ?? Number.MAX_SAFE_INTEGER;
        const bOrder = orderMap.get(bApiKey) ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
    }

    return allColumns;
  }, [
    updateContact,
    handlePhoneAdd,
    handlePhoneDelete,
    handleEmailAdd,
    handleEmailDelete,
    handleCompanySelect,
    handleCompanyClick,
    handleCreateCompanyForContact,
    getColumnLabel,
    isColumnVisible,
    apiColumns,
    attributeConfig,
    tagsData,
    t,
  ]);

  type FilterResponse = {
    data: Contact[];
    metadata?: {
      current_page?: number;
      page_size?: number;
      has_next_page?: boolean;
    };
  };

  const serverFilter = useServerFilter<Contact, FilterResponse>({
    queryKey: ['contacts', 'filter', sortField, sortOrder],
    queryFn: async ({ filter, pagination }) => {
      const response = await $fetchAllo.POST('/v2/contact/filter', {
        params: {
          query: { extend: 'custom_properties' },
        } as any,
        body: {
          filter: {
            and: filter.and as components['schemas']['ContactFilterCondition'][],
          },
          sort: [
            {
              field: sortField as
                | 'name'
                | 'last_name'
                | 'website'
                | 'job_title'
                | 'company'
                | 'created_at'
                | 'updated_at'
                | 'last_activity_date',
              order: sortOrder,
            },
            { field: 'created_at', order: 'desc' },
          ],
          pagination: { page: pagination.page, size: pagination.pageSize },
        },
      });
      const result = response.data as FilterResponse | undefined;
      return {
        data: result?.data ?? [],
        metadata: result?.metadata,
      };
    },
    select: (response) => response.data,
    getNextPageParam: (lastPage) => {
      const metadata = lastPage?.metadata;
      if (!metadata) {
        return undefined;
      }
      const currentPage = metadata.current_page ?? 0;
      return metadata.has_next_page ? currentPage + 1 : undefined;
    },
    initialFilter: initialServerFilter,
    pageSize: 50,
    forceEnabled: isCustomSort,
  });

  const handleServerFilter = useCallback(
    (filter: ServerFilter, rawFilters?: FilterValue[]) => {
      serverFilter.applyFilter(filter);

      // Use raw FilterValue[] to preserve custom conditions (from, to) that would
      // be lost in the ServerFilter → UrlFilter round-trip via mapOperatorToCondition
      const newFilters: UrlFilter[] = rawFilters
        ? rawFilters
            .filter((f) => f.condition !== null)
            .map((f) => ({
              column: f.columnId,
              condition: f.condition as FilterCondition,
              value: f.value,
            }))
        : filter.and
            .filter((c) => c.field && c.operator)
            .map((c) => ({
              column: c.field,
              condition: mapOperatorToCondition(c.operator),
              value: c.value ? String(c.value) : undefined,
            }));

      if (newFilters.length > 0) {
        onNavigate((prev) => ({
          ...prev,
          filters: JSON.stringify(newFilters),
        }));
      } else {
        onNavigate((prev) => ({
          sortField: prev.sortField,
          sortOrder: prev.sortOrder,
        }));
      }
    },
    [onNavigate, serverFilter.applyFilter],
  );

  const handleFilterEvent = useCallback((event: FilterEvent) => {
    const eventName =
      event.type === 'applied'
        ? ANALYTICS_EVENTS.FILTERS.APPLIED
        : ANALYTICS_EVENTS.FILTERS.REMOVED;
    analytics.track(eventName, {
      page: 'contacts',
      filter_column: event.filter.columnId,
      filter_condition: event.filter.condition,
      filter_value: event.filter.value,
      active_filters: event.activeFilters.map((f) => f.columnId),
      active_filter_count: event.activeFilters.length,
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    analytics.track(ANALYTICS_EVENTS.FILTERS.CLEARED, { page: 'contacts' });
    serverFilter.clearFilter();
    onNavigate((prev) => ({
      sortField: prev.sortField,
      sortOrder: prev.sortOrder,
    }));
  }, [onNavigate, serverFilter.clearFilter]);

  const handleSortChange = useCallback(
    (newField: string, newOrder: SortOrder) => {
      const activeConditions = serverFilter.filter?.and ?? [];
      analytics.track(ANALYTICS_EVENTS.FILTERS.SORTING_APPLIED, {
        page: 'contacts',
        sort_field: newField,
        sort_order: newOrder,
        active_filters: activeConditions.map((f) => f.field),
        active_filter_count: activeConditions.length,
      });
      onNavigate((prev) => ({
        ...prev,
        sortField: newField,
        sortOrder: newOrder,
      }));
    },
    [onNavigate, serverFilter.filter],
  );

  const contactQuery = useContactsQuery({ enabled: !serverFilter.isActive });

  const showLoading = serverFilter.isActive
    ? serverFilter.isLoading || (serverFilter.isFetching && serverFilter.isPlaceholderData)
    : contactQuery.isLoading;

  const contacts = useMemo(() => {
    if (serverFilter.isActive) {
      const seen = new Set<string>();
      return serverFilter.data.filter((contact) => {
        if (!contact.id || seen.has(contact.id)) {
          return false;
        }
        seen.add(contact.id);
        return true;
      });
    }

    if (!contactQuery.data?.pages) {
      return [];
    }
    const allContacts = contactQuery.data.pages.flatMap((page) => (page?.data as Contact[]) ?? []);
    const seen = new Set<string>();
    return allContacts.filter((contact) => {
      if (!contact.id || seen.has(contact.id)) {
        return false;
      }
      seen.add(contact.id);
      return true;
    });
  }, [serverFilter.isActive, serverFilter.data, contactQuery.data?.pages]);

  const selectedContacts = useMemo(
    () => contacts.filter((c) => c.id && selection[c.id]),
    [contacts, selection],
  );

  // One-shot demo preselect: when the user lands from the Power Dialer empty-state
  // CTA, the URL carries ?preselectDemo=true. Once contacts have loaded, check the
  // first 5 so the user sees the push-to-Power-Dialer floating bar without having
  // to discover row selection themselves.
  // `selection` is read off a ref so the effect doesn't re-run on every toggle
  // (it only needs to know whether the user already touched selection at the
  // moment of the one-shot fire).
  const hasPreselectedDemoRef = useRef(false);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  useEffect(() => {
    if (hasPreselectedDemoRef.current) {
      return;
    }
    if (!searchParams.preselectDemo) {
      return;
    }
    if (contacts.length === 0) {
      return;
    }
    hasPreselectedDemoRef.current = true;

    const userAlreadySelected = Object.keys(selectionRef.current).length > 0;
    if (!userAlreadySelected) {
      const idsToSelect = contacts
        .slice(0, 5)
        .map((c) => c.id)
        .filter((id): id is string => !!id);
      if (idsToSelect.length > 0) {
        setSelection(Object.fromEntries(idsToSelect.map((id) => [id, true])));
        toast(t('components.contacts.demoPreselect.toast'));
      }
    }

    onNavigate((prev) => ({ ...prev, preselectDemo: undefined }));
  }, [searchParams.preselectDemo, contacts, onNavigate, t]);

  const selectedRowClassName = useCallback(
    (row: { original: Contact }) =>
      row.original.id && selection[row.original.id] ? '[&_.bg-white]:!bg-highlight-light' : '',
    [selection],
  );

  const toggleRowSelection = useCallback((contactId: string, next: boolean) => {
    setSelection((prev) => ({ ...prev, [contactId]: next }));
  }, []);

  const toggleAllRowsSelection = useCallback(
    (next: boolean) => {
      setSelection(
        next
          ? Object.fromEntries(
              contacts
                .map((c) => c.id)
                .filter((id): id is string => Boolean(id))
                .map((id) => [id, true]),
            )
          : {},
      );
    },
    [contacts],
  );

  const clearRowSelection = useCallback(() => {
    setSelection({});
  }, []);

  const visibleSelectionCounts = useMemo(() => {
    let total = 0;
    let selected = 0;
    for (const c of contacts) {
      if (!c.id) {
        continue;
      }
      total++;
      if (selection[c.id]) {
        selected++;
      }
    }
    return { total, selected };
  }, [contacts, selection]);

  const handleOpenSidepanel = useCallback(
    (id?: string) => {
      const contact = id ? contacts.find((c) => c.id === id) : undefined;
      const contactName = contact
        ? [contact.name, contact.last_name].filter(Boolean).join(' ')
        : undefined;

      setSidePanelState({
        open: true,
        mode: 'contacts',
        context: {
          contactId: id ?? 'new',
        },
        entityInfo: contactName ? { name: contactName } : undefined,
      });

      if (id) {
        const currentIndex = contacts.findIndex((c) => c.id === id);
        if (currentIndex !== -1) {
          const nextContacts = contacts.slice(currentIndex + 1, currentIndex + 6);
          for (const contact of nextContacts) {
            if (contact.id) {
              queryClient.prefetchQuery(
                $apiAllo.queryOptions('get', '/v1/contact/{contact_id}', {
                  params: {
                    path: { contact_id: contact.id },
                  },
                }),
              );
              queryClient.prefetchQuery(
                $apiAllo.queryOptions('get', '/v1/contact/{contactId}/activity', {
                  params: {
                    path: { contactId: contact.id },
                  },
                }),
              );
            }
          }
        }
      }
    },
    [setSidePanelState, contacts, queryClient],
  );
  handleOpenSidepanelRef.current = handleOpenSidepanel;

  const selectionContextValue = useMemo<SelectionContextValue>(
    () => ({
      isSelected: (id: string) => !!selection[id],
      toggle: toggleRowSelection,
      toggleAll: toggleAllRowsSelection,
      total: visibleSelectionCounts.total,
      selected: visibleSelectionCounts.selected,
    }),
    [
      selection,
      toggleRowSelection,
      toggleAllRowsSelection,
      visibleSelectionCounts.total,
      visibleSelectionCounts.selected,
    ],
  );

  return (
    <SelectionContext.Provider value={selectionContextValue}>
      <CreateContactModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
      <CreateCompanyModal
        open={isCreateCompanyModalOpen}
        onOpenChange={setIsCreateCompanyModalOpen}
        onSuccess={handleCompanyCreated}
        initialTeamMemberIds={createCompanyForContact ? [createCompanyForContact.id] : undefined}
        initialTeamMembers={createCompanyForContact ? [createCompanyForContact] : undefined}
      />
      <PageLayout className="relative">
        <PageHeader
          title={
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="box-content shrink-0 border-x-2 border-x-transparent bg-clip-padding text-heading-13 text-primary-100">
                {t('components.contacts.name')}
              </span>
              <ContactViewTabs
                views={views}
                activeViewId={activeViewId}
                onViewSelect={handleViewSelect}
                onCreateView={handleCreateView}
                onRenameView={handleRenameView}
                onDuplicateView={handleDuplicateView}
                onDeleteView={handleDeleteView}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            </div>
          }
          rightAddOn={
            <Button
              variant="outline"
              size="sm"
              iconName={Icons.Plus}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t('components.contacts.table.createContact')}
            </Button>
          }
        />
        <PageContent>
          <VirtualTable
            data={contacts}
            columns={columns}
            height="100%"
            enableSorting
            enableColumnResizing
            enableColumnReorder
            rowClassName={selectedRowClassName}
            onColumnReorder={handleColumnReorder}
            columnPinning={{
              left: ['name', 'phone'],
            }}
            tableControl={
              <SortButton
                field={sortField}
                order={sortOrder}
                onSortChange={handleSortChange}
                columns={sortableColumns}
              />
            }
            filterableColumns={filterableColumns}
            columnConfigs={columnConfigs}
            manualFiltering
            onServerFilter={handleServerFilter}
            onFilterEvent={handleFilterEvent}
            initialFilters={initialFilters}
            hasActiveFilters={(serverFilter.filter?.and.length ?? 0) > 0}
            onClearFilters={handleClearFilters}
            filterTrailingContent={
              <div className="flex items-center gap-2">
                <ShowColumnsButton
                  columns={apiColumns}
                  entity="contact"
                  iconConfig={CONTACT_COLUMN_ICONS}
                  translationConfig={CONTACT_TRANSLATION_KEYS}
                  pinnedColumns={['name', 'phone_numbers']}
                  defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                />
                {activeViewId === null && (serverFilter.filter?.and.length ?? 0) > 0 ? (
                  <button
                    type="button"
                    onClick={() => setIsSaveViewDialogOpen(true)}
                    className="flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-full border border-highlight-default bg-highlight-default/10 px-3 text-highlight-default transition-colors hover:bg-highlight-default/20"
                  >
                    <P3 className="font-medium">{t('components.contacts.views.saveView')}</P3>
                    <Bookmark className="size-3" />
                  </button>
                ) : activeViewId !== null && hasUnsavedChanges ? (
                  <button
                    type="button"
                    onClick={handleSaveChanges}
                    disabled={updateViewMutation.isPending}
                    className="flex h-7 shrink-0 cursor-pointer items-center gap-1 rounded-full border border-highlight-default bg-highlight-default/10 px-3 text-highlight-default transition-colors hover:bg-highlight-default/20 disabled:opacity-50"
                  >
                    <P3 className="font-medium">{t('components.contacts.views.saveChanges')}</P3>
                    <Bookmark className="size-3" />
                  </button>
                ) : null}
              </div>
            }
            isLoading={showLoading}
            getRowId={(row) => row.id ?? ''}
            onLoadMore={
              serverFilter.isActive ? serverFilter.fetchNextPage : contactQuery.fetchNextPage
            }
            hasNextPage={
              serverFilter.isActive ? serverFilter.hasNextPage : contactQuery.hasNextPage
            }
            isFetchingNextPage={
              serverFilter.isActive
                ? serverFilter.isFetchingNextPage
                : contactQuery.isFetchingNextPage
            }
            overscan={20}
            onCellActivate={(_rowIndex, columnIndex, row) => {
              const columnId = columns[columnIndex]?.id;
              if (columnId === 'name') {
                handleOpenSidepanel(row.original.id);
              } else if (columnId === 'phone' && row.original.id) {
                phoneCellRefs.current.get(row.original.id)?.open();
              } else if (columnId === 'emails' && row.original.id) {
                emailCellRefs.current.get(row.original.id)?.open();
              } else if (columnId === 'company' && row.original.id) {
                companyCellRefs.current.get(row.original.id)?.open();
              } else if (columnId && row.original.id) {
                const refKey = `${row.original.id}:${columnId}`;
                dateCellRefs.current.get(refKey)?.open();
                tagCellRefs.current.get(refKey)?.open();
              }
            }}
          />
        </PageContent>
        <PushToPowerDialerBar selectedContacts={selectedContacts} onClear={clearRowSelection} />
      </PageLayout>

      {/* View dialogs */}
      <SaveViewDialog
        open={isSaveViewDialogOpen}
        onOpenChange={(open) => {
          setIsSaveViewDialogOpen(open);
          if (!open) {
            setViewToDuplicate(null);
          }
        }}
        onSave={handleSaveNewView}
        onDuplicate={handleDuplicateFromDialog}
        viewToDuplicate={viewToDuplicate}
        isLoading={createViewMutation.isPending || duplicateViewMutation.isPending}
      />

      <RenameViewDialog
        open={isRenameViewDialogOpen}
        onOpenChange={(open) => {
          setIsRenameViewDialogOpen(open);
          if (!open) {
            setViewToRename(null);
          }
        }}
        view={viewToRename}
        onRename={handleRenameSubmit}
        isLoading={updateViewMutation.isPending}
      />

      <ConfirmationDialog
        open={isDeleteConfirmationOpen}
        onOpenChange={(open) => setIsDeleteConfirmationOpen(open ?? false)}
        title={t('components.contacts.views.deleteConfirmation.title')}
        description={t('components.contacts.views.deleteConfirmation.description')}
        confirmLabel={t('components.contacts.views.deleteConfirmation.confirm')}
        cancelLabel={t('components.contacts.views.deleteConfirmation.cancel')}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteViewMutation.isPending}
        variant="destructive"
      />
    </SelectionContext.Provider>
  );
}
